/**
 * Recording a voice note in the browser.
 *
 * The two engines disagree and neither can be talked round: Chrome records
 * WebM/Opus and refuses MP4, Safari records MP4/AAC and refuses WebM. Since
 * Safari also cannot *play* WebM, whatever comes out of here is transcoded to
 * m4a server-side before anyone hears it. So this picks whichever container
 * the browser will actually give us and leaves the rest to transcodeAudio.
 */

/** In the order we would like them, best first. */
const CANDIDATES = [
  'audio/mp4',
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
];

export function canRecord(): boolean {
  return (
    typeof MediaRecorder !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia
  );
}

function pickType(): string | undefined {
  for (const type of CANDIDATES) {
    if (MediaRecorder.isTypeSupported(type)) return type;
  }
  // Undefined lets the browser choose; every engine that has MediaRecorder at
  // all has some default, and the server takes the content type from the blob.
  return undefined;
}

export interface Recording {
  blob: Blob;
  durationSec: number;
}

export interface Recorder {
  /** Resolves with the recording, or null if it was cancelled or silent. */
  stop: () => Promise<Recording | null>;
  cancel: () => void;
}

/**
 * Starts recording immediately. The caller is expected to have asked the
 * person first, because this triggers the microphone permission prompt.
 */
export async function startRecording(): Promise<Recorder> {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const mimeType = pickType();
  const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
  const chunks: Blob[] = [];
  const startedAt = Date.now();
  let cancelled = false;

  recorder.ondataavailable = (event) => {
    if (event.data.size > 0) chunks.push(event.data);
  };
  recorder.start();

  const release = () => {
    for (const track of stream.getTracks()) track.stop();
  };

  return {
    stop: () =>
      new Promise<Recording | null>((resolve) => {
        if (recorder.state === 'inactive') {
          release();
          resolve(null);
          return;
        }
        recorder.onstop = () => {
          release();
          if (cancelled || !chunks.length) {
            resolve(null);
            return;
          }
          const type = recorder.mimeType || chunks[0].type || 'audio/webm';
          resolve({
            blob: new Blob(chunks, { type }),
            durationSec: Math.max(1, Math.round((Date.now() - startedAt) / 1000)),
          });
        };
        recorder.stop();
      }),
    cancel: () => {
      cancelled = true;
      if (recorder.state !== 'inactive') recorder.stop();
      else release();
    },
  };
}

/**
 * The content type the server is told about. A recorder reports things like
 * `audio/webm;codecs=opus`, and the extension table is keyed on the bare type.
 */
export function baseType(blobType: string): string {
  return blobType.split(';')[0] || 'audio/webm';
}
